import React, { useState } from "react";

// ── Constants ──────────────────────────────────────────────────────────────────

const ASSOCIATION_FIELDS = [
  { key: "accredited_investor", label: "Accredited Investor" },
  { key: "qualified_purchaser", label: "Qualified Purchaser" },
  { key: "us_broker_dealer_association", label: "US Broker Dealer Association" },
  { key: "broker_dealer_employee", label: "Broker Dealer Employee" },
  { key: "ria_association", label: "RIA Association" },
  { key: "shareholder_or_member_board_director", label: "Shareholder or Board Member of Public Company" },
  { key: "low_priced_securities", label: "Trades Low-Priced Securities" },
];

// ── Loading Spinner ────────────────────────────────────────────────────────────

const Spinner = () => (
  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

// ── Toggle Button Component ────────────────────────────────────────────────────

const ToggleButtons = ({ value, onChange, disabled = false }) => {
  const isYes = value === true;
  const isNo = value === false;

  return (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={() => onChange(true)}
        disabled={disabled}
        className={`px-4 py-2 rounded-md font-medium transition-colors ${
          isYes
            ? "bg-emerald-500 text-white"
            : "bg-white border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        }`}
      >
        Yes
      </button>
      <button
        type="button"
        onClick={() => onChange(false)}
        disabled={disabled}
        className={`px-4 py-2 rounded-md font-medium transition-colors ${
          isNo
            ? "bg-red-500 text-white"
            : "bg-white border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        }`}
      >
        No
      </button>
    </div>
  );
};

// ── InvestmentObjectiveForm ────────────────────────────────────────────────────

const InvestmentObjectiveForm = ({
  householdData,
  fieldData,
  missingFields,
  onSave,
  onSaveAndExit,
  saving,
  nextLabel,
}) => {
  // Get existing investor_associations from fieldData
  const existingAssociations = fieldData?.investor_associations || {};

  // Determine which fields to show (only those with null/undefined values)
  const fieldsToShow = ASSOCIATION_FIELDS.filter((f) => {
    const existingValue = existingAssociations[f.key];
    return existingValue === null || existingValue === undefined;
  });

  // Initialize form state with false for each field to show
  const [formData, setFormData] = useState(() => {
    const initial = {};
    fieldsToShow.forEach((f) => {
      initial[f.key] = false;
    });
    return initial;
  });

  // Handle field change
  const handleFieldChange = (fieldKey, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  // Build and validate payload
  const buildPayload = () => {
    const associations = { ...existingAssociations };
    fieldsToShow.forEach((f) => {
      associations[f.key] = formData[f.key] ?? false;
    });
    return {
      household: {
        investor_associations: associations,
      },
    };
  };

  // Count required fields still missing (all fields shown are required)
  const requiredFieldsMissing = fieldsToShow.length;

  // Handle save
  const handleSave = async () => {
    const payload = buildPayload();
    onSave(payload);
  };

  // Handle save and exit
  const handleSaveAndExit = async () => {
    const payload = buildPayload();
    onSaveAndExit(payload);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Missing Information</h2>
        {requiredFieldsMissing > 0 && (
          <span className="text-sm text-red-500 font-medium">
            {requiredFieldsMissing} Required field{requiredFieldsMissing !== 1 ? "s" : ""} missing
          </span>
        )}
      </div>

      {/* Form fields */}
      <div className="space-y-4">
        {fieldsToShow.length > 0 ? (
          fieldsToShow.map((field) => (
            <div
              key={field.key}
              className="p-4 bg-gray-50 rounded-lg flex items-center justify-between"
            >
              <label className="text-sm font-medium text-gray-700">{field.label}</label>
              <ToggleButtons
                value={formData[field.key]}
                onChange={(value) => handleFieldChange(field.key, value)}
                disabled={saving}
              />
            </div>
          ))
        ) : (
          <div className="text-center py-6">
            <p className="text-sm text-gray-500 italic">All investment objective fields have been completed.</p>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="mt-8 pt-6 border-t border-gray-200 flex justify-between">
        <button
          type="button"
          onClick={handleSaveAndExit}
          disabled={saving}
          className="px-6 py-2 rounded-md font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Save & Exit
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2 rounded-md font-medium text-white bg-emerald-500 hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving && <Spinner />}
          {nextLabel || "Save & Next →"}
        </button>
      </div>
    </div>
  );
};

export default InvestmentObjectiveForm;
